"use client"
import { useRef } from 'react'
import { type PlanId, type ResolvedBranchNode } from '@/lib/branchTypes'
import { useMediaQuery } from '@/lib/useMediaQuery'
import {
  BRANCH_CHOICES_VIEWPORT_FRACTION,
  PLAYBOOK_BRANCH_FLOWCHART_ID,
} from './choicePointAnchor'
import { ChooseYourPathOG } from './ChooseYourPathOG'
import { BranchChoicePreviewStack } from './playbook-branch-choices/BranchChoiceMarkdownPreview'
import {
  PLAN_A_PREVIEW_FADE_END_VH,
  PlanAPreviewSubtree,
} from './playbook-branch-choices/PlanAPreviewSubtree'
import { DesktopPlaybookBranchChoicesFive } from './playbook-branch-choices/DesktopPlaybookBranchChoicesFive'
import { DesktopPlaybookBranchChoicesTwo } from './playbook-branch-choices/DesktopPlaybookBranchChoicesTwo'
import { MobilePlaybookBranchChoicesFive } from './playbook-branch-choices/MobilePlaybookBranchChoicesFive'
import { MobilePlaybookBranchChoicesTwo } from './playbook-branch-choices/MobilePlaybookBranchChoicesTwo'
import { getDefaultPrimaryTargetId } from './playbook-branch-choices/getDefaultPrimaryTargetId'
import { useBranchChoiceHoverPreview } from './playbook-branch-choices/useBranchChoiceHoverPreview'
import { useDefaultPrimarySelection } from './playbook-branch-choices/useDefaultPrimarySelection'
import { useStuckHoverSafetyNet } from './playbook-branch-choices/useStuckHoverSafetyNet'
import {
  type PlaybookBranchChoicesGridProps,
  type PlaybookBranchChoicesProps,
} from './playbook-branch-choices/playbookBranchChoicesTypes'

const BRANCH_PREVIEW_HEIGHT_PX = 300
const BRANCH_PREVIEW_HEIGHT_MOBILE_PX = 200

// Stable DOM anchor for a node's branch-choice buttons, so selecting a branch
// can scroll the buttons (rather than the revealed child node) into position.
export const getBranchChoicesElementId = (nodeId: string) => `branch-choices-${nodeId}`

// How many frames to wait for the click's re-render to make the resting spot
// reachable before firing the scroll anyway (~1.5s at 60fps).
const SCROLL_REACHABLE_MAX_FRAMES = 90

// Smoothly scroll a node's branch-choice buttons to a fixed viewport position.
// Called on click. The click's re-render can change the scrollable height out
// from under us — before a plan is picked, ChoicePointScrollLock caps the
// document just past the choice point, and picking a plan swaps in a whole
// subtree — and a smooth scroll aimed past the current max scroll clamps at
// call time and never completes. So wait (bounded) until the target is
// actually reachable, then fire a single smooth scroll.
export const scrollBranchChoicesIntoView = (nodeId: string) => {
  if (typeof window === "undefined") return
  const elementId = getBranchChoicesElementId(nodeId)
  let frames = 0
  const tick = () => {
    // Re-resolve each attempt: the selection re-render could swap the node.
    const element = document.getElementById(elementId)
    if (!element) {
      if (++frames <= SCROLL_REACHABLE_MAX_FRAMES) requestAnimationFrame(tick)
      return
    }
    const elementTop = element.getBoundingClientRect().top + window.scrollY
    const target = Math.max(0, elementTop - window.innerHeight * BRANCH_CHOICES_VIEWPORT_FRACTION)
    const maxScrollTop = document.documentElement.scrollHeight - window.innerHeight
    if (target > maxScrollTop && ++frames <= SCROLL_REACHABLE_MAX_FRAMES) {
      requestAnimationFrame(tick)
      return
    }
    window.scrollTo({ top: target, behavior: "smooth" })
  }
  tick()
}

// How the deselect settle-scroll decides it has arrived: scrollY holds within
// EPSILON of the target, motion stops early (the reader grabbed the wheel), or
// the frame budget runs out (~2s at 60fps) — whichever comes first.
const DESELECT_SETTLE_EPSILON_PX = 2
const DESELECT_SETTLE_STILL_FRAMES = 8
const DESELECT_SETTLE_MAX_FRAMES = 120

// Deselecting a plan re-engages the choice-point scroll lock
// (ChoicePointScrollLock), which caps the document at the preview band's
// fade-end and INSTANTLY clamps any viewport already past the new bottom — a
// jump cut. So deselection runs in two beats: smooth-scroll to the deselected
// resting spot first (the usual buttons-at-40%-viewport position, but never
// past where the future cap will land), and only call `deselect` (which
// truncates the path) once the viewport has settled inside those bounds.
// Returns a canceller so a follow-up click can abort a still-pending deselect.
export const settleScrollThenDeselect = (
  nodeId: string,
  deselect: () => void
): (() => void) => {
  let cancelled = false
  const cancel = () => { cancelled = true }
  if (typeof window === "undefined") return cancel
  const element = document.getElementById(getBranchChoicesElementId(nodeId))
  if (!element) {
    deselect()
    return cancel
  }
  const rect = element.getBoundingClientRect()
  const desired = Math.max(
    0,
    rect.top + window.scrollY - window.innerHeight * BRANCH_CHOICES_VIEWPORT_FRACTION
  )
  // Where the lock will cap scrolling once the path truncates: the preview
  // band mounts directly under the buttons and the cap lands at its fade-end
  // (see ChoicePointScrollLock's applyCap). Content after the capped section
  // only makes the real max larger, so this estimate errs on the safe side.
  const futureMaxScrollTop =
    rect.bottom + window.scrollY
    + (PLAN_A_PREVIEW_FADE_END_VH / 100) * window.innerHeight
    - window.innerHeight
  const target = Math.max(0, Math.min(desired, futureMaxScrollTop))
  if (Math.abs(window.scrollY - target) <= DESELECT_SETTLE_EPSILON_PX) {
    deselect()
    return cancel
  }
  window.scrollTo({ top: target, behavior: "smooth" })
  let frames = 0
  let stillFrames = 0
  let lastScrollY = window.scrollY
  const tick = () => {
    if (cancelled) return
    stillFrames = Math.abs(window.scrollY - lastScrollY) < 1 ? stillFrames + 1 : 0
    lastScrollY = window.scrollY
    const arrived = Math.abs(window.scrollY - target) <= DESELECT_SETTLE_EPSILON_PX
    if (arrived || stillFrames >= DESELECT_SETTLE_STILL_FRAMES || ++frames > DESELECT_SETTLE_MAX_FRAMES) {
      deselect()
      return
    }
    requestAnimationFrame(tick)
  }
  requestAnimationFrame(tick)
  return cancel
}

// Picks the active plan node when laid out for the expanded-plan view (currently only consumed by ExpandedPlanInfoRow, which is commented out below).
const resolveActivePlanNode = (
  expandedPlanLayout: boolean,
  hoveredTargetId: string | null,
  nextNodeId: string | null,
  options: PlaybookBranchChoicesProps['options'],
  nodes: Record<string, ResolvedBranchNode>,
): { activePlanNode: ResolvedBranchNode | null; activePlan: PlanId } => {
  if (!expandedPlanLayout) return { activePlanNode: null, activePlan: 'A' }
  const optionTargetNodes = options
    .map(o => (o.targetId ? nodes[o.targetId] : null))
    .filter((n): n is ResolvedBranchNode => !!n)
  const hoveredPlanNode = hoveredTargetId ? nodes[hoveredTargetId] : null
  const activePlanNode = hoveredPlanNode || (nextNodeId && nodes[nextNodeId]) || optionTargetNodes[0] || null
  return { activePlanNode, activePlan: activePlanNode?.planId ?? 'A' }
}

export const PlaybookBranchChoices = ({
  nodeId,
  options,
  nextNodeId,
  selectedPath,
  currentPathIndex,
  nodes,
  onSelect,
  onDefaultSelect,
  onPlanHover,
  onBranchHover,
  showPerspectiveAboveFlowchart,
  selectedPerspective,
  onSelectPerspective,
}: PlaybookBranchChoicesProps) => {
  const isMobile = useMediaQuery('(max-width: 900px)')

  const { hoveredTargetId, previewTargetId, previewNode, handleBranchHover, clearPreview } =
    useBranchChoiceHoverPreview({ nextNodeId, nodes, onBranchHover })

  const defaultPrimaryTargetId = getDefaultPrimaryTargetId({
    nodeId,
    options,
    nextNodeId,
    selectedPath,
    nodes,
  })

  useDefaultPrimarySelection(defaultPrimaryTargetId, onDefaultSelect)

  const branchChoicesWrapperRef = useRef<HTMLDivElement | null>(null)
  useStuckHoverSafetyNet({
    isHovering: hoveredTargetId !== null,
    gridRef: branchChoicesWrapperRef,
    onClear: () => {
      handleBranchHover(null)
      onPlanHover?.(null)
    },
  })

  if (options.length === 0) return null

  const expandedPlanLayout = !!nodes[nodeId]?.expandedPlanLayout
  const isPrimaryBranchPoint = options.some(
    o => o.primary || (o.targetId && nodes[o.targetId]?.primary)
  )
  const isTwoChoice = options.length <= 2
  const primaryOption = options.find(
    o => o.targetId && (o.primary || nodes[o.targetId]?.primary)
  )
  const showPlanAPreview = expandedPlanLayout && !nextNodeId && !!primaryOption?.targetId

  const branchPreviewHeightPx = isMobile ? BRANCH_PREVIEW_HEIGHT_MOBILE_PX : BRANCH_PREVIEW_HEIGHT_PX

  const { activePlan, activePlanNode } = resolveActivePlanNode(
    expandedPlanLayout, hoveredTargetId, nextNodeId, options, nodes
  )

  const showPrimaryHoverStyleByDefault = !nextNodeId && !hoveredTargetId

  const gridProps: PlaybookBranchChoicesGridProps = {
    nodeId,
    options,
    nextNodeId,
    selectedPath,
    currentPathIndex,
    nodes,
    onSelect,
    onPlanHover,
    onBranchHover: handleBranchHover,
    defaultPrimaryTargetId,
    expandedPlanLayout,
    onGridMouseLeave: clearPreview,
    showPrimaryHoverStyleByDefault,
  }
  return (
    <div
      className={`${isPrimaryBranchPoint ? '' : 'mt-24'} ${!nextNodeId ? 'md:-mb-32' : ''}`}
      id={expandedPlanLayout ? PLAYBOOK_BRANCH_FLOWCHART_ID : undefined}
    >
      {/* {expandedPlanLayout && (
        <ExpandedPlanInfoRow activePlan={activePlan} activePlanNode={activePlanNode} />
      )} */}
      {expandedPlanLayout && (
        <ChooseYourPathOG
          hoveredPlanIndex={(() => {
            const i = options.findIndex(o => o.targetId && o.targetId === hoveredTargetId)
            return i === -1 ? null : i
          })()}
        />
      )}
      <div id={getBranchChoicesElementId(nodeId)} ref={branchChoicesWrapperRef}>
        {isMobile && isTwoChoice ? (
          <MobilePlaybookBranchChoicesTwo {...gridProps} />
        ) : isMobile ? (
          <MobilePlaybookBranchChoicesFive {...gridProps} />
        ) : isTwoChoice ? (
          <DesktopPlaybookBranchChoicesTwo {...gridProps} />
        ) : (
          <DesktopPlaybookBranchChoicesFive {...gridProps} />
        )}
      </div>
      {selectedPath.length < 2 && !showPlanAPreview && (
        <BranchChoicePreviewStack
          previewTargetId={previewTargetId}
          previewNode={previewNode}
          branchPreviewHeightPx={branchPreviewHeightPx}
        />
      )}
      {showPlanAPreview && primaryOption?.targetId && (
        <PlanAPreviewSubtree
          hoveredNodeId={hoveredTargetId}
          nodes={nodes}
        />
      )}
    </div>
  )
}
